import { useState, useRef, useEffect } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ME } from '../graphql/auth';
import { GET_ITEMS_BAJO_STOCK } from '../graphql/inventario';
import { useTheme } from '../contexts/ThemeContext';
import LogoLight from '../assets/logo-posfac-dark.png';
import LogoDark from '../assets/logo-posfac.png';
import {
  LayoutDashboard,
  BedDouble,
  CalendarCheck,
  ClipboardList,
  Users,
  User,
  BookOpen,
  Briefcase,
  Package,
  ShoppingCart,
  CreditCard,
  FileText,
  Printer,
  BarChart3,
  Settings,
  Sun,
  Moon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  KeyRound,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  DollarSign,
  Award,
  Building2,
  ArrowLeftRight,
  TrendingDown,
  Receipt,
  ShoppingBag,
  Truck
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();
  const { data: userData, loading: userLoading } = useQuery(ME);
  const { data: stockData } = useQuery(GET_ITEMS_BAJO_STOCK, {
    pollInterval: 60000
  });

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const userMenuRef = useRef(null);
  const submenuRefs = useRef({});

  const user = userData?.me;
  const itemsBajoStock = stockData?.itemsBajoStock?.length || 0;

  // Persistir estado colapsado
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  // Cerrar menus al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      // Cerrar submenu abierto si click fuera de su ref
      if (openSubmenu && submenuRefs.current[openSubmenu]) {
        if (!submenuRefs.current[openSubmenu].contains(e.target)) {
          setOpenSubmenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openSubmenu]);

  // Cerrar sidebar mobile al navegar
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const propiedadNombre = localStorage.getItem('propiedad_nombre') || 'Sin propiedad';

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('propiedad_id');
    localStorage.removeItem('propiedad_nombre');
    navigate('/login');
  };

  const handleCambiarPropiedad = () => {
    localStorage.removeItem('propiedad_id');
    localStorage.removeItem('propiedad_nombre');
    navigate('/seleccionar-propiedad');
  };

  const toggleCollapse = () => {
    setCollapsed(prev => !prev);
    setOpenSubmenu(null);
    setShowUserMenu(false);
  };

  const userRol = user?.rol;
  const permisos = user?.permisos_efectivos || [];
  const isAdminOrSuper = userRol === 'admin' || userRol === 'superadmin';

  const tieneAccesoModulo = (modulo) => {
    if (isAdminOrSuper) return true;
    return permisos.some(p => p.startsWith(`${modulo}.`));
  };

  const allMenuItems = [
    { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', modulo: 'dashboard' },
    { title: 'Reservas', icon: CalendarCheck, path: '/reservas', modulo: 'reservas' },
    { title: 'Grados Militares', icon: Award, path: '/grados-militares' },
    { title: 'Hospedajes', icon: ClipboardList, path: '/hospedajes', modulo: 'hospedajes' },
    { title: 'Cuentas de Cobro', icon: Receipt, path: '/cuentas-cobro', modulo: 'hospedajes' },
    {
      title: 'Clientes',
      icon: Users,
      hasSubmenu: true,
      modulo: 'clientes',
      subItems: [
        { title: 'Clientes', icon: User, path: '/clientes' },
        { title: 'Libro de Huéspedes', icon: BookOpen, path: '/huespedes' },
      ],
    },
    { title: 'Servicios', icon: Briefcase, path: '/servicios', modulo: 'consumos' },
    {
      title: 'Productos',
      icon: Package,
      path: '/productos',
      badge: itemsBajoStock > 0 ? itemsBajoStock : null,
      modulo: 'inventario',
    },
    {
      title: 'POS',
      icon: ShoppingCart,
      hasSubmenu: true,
      modulo: 'pos',
      subItems: [
        { title: 'Punto de Venta', icon: ShoppingCart, path: '/pos' },
        { title: 'Gestión de Caja', icon: CreditCard, path: '/caja' },
      ],
    },
    {
      title: 'Egresos',
      icon: TrendingDown,
      hasSubmenu: true,
      modulo: 'caja',
      subItems: [
        { title: 'Gastos', icon: Receipt, path: '/gastos' },
        { title: 'Compras', icon: ShoppingBag, path: '/compras' },
        { title: 'Proveedores', icon: Truck, path: '/proveedores' },
      ],
    },
    { title: 'PosBox', icon: FileText, path: '/factubox', modulo: 'factubox' },
    { title: 'Reportes', icon: BarChart3, path: '/reportes', modulo: 'reportes' },
    { title: 'Configuración', icon: Settings, path: '/configuracion', modulo: 'configuracion' },
  ];

  // Filtrar items según permisos del usuario
  const menuItems = allMenuItems.filter(item => {
    if (!item.modulo) return true; // Sin restricción
    return tieneAccesoModulo(item.modulo);
  });

  const renderNavItem = (item) => {
    const Icon = item.icon;

    // Item con submenu (POS, Clientes, etc.)
    if (item.hasSubmenu) {
      const isAnyActive = item.subItems.some(
        sub => location.pathname === sub.path
      );
      const isOpen = openSubmenu === item.title;

      return (
        <li key={item.title} className="sidebar__menu-item" ref={el => { submenuRefs.current[item.title] = el; }}>
          <button
            className={`sidebar__link sidebar__link--has-submenu ${isAnyActive ? 'sidebar__link--active' : ''}`}
            onClick={() => setOpenSubmenu(prev => prev === item.title ? null : item.title)}
            data-tooltip={item.title}
          >
            <Icon className="sidebar__icon" size={20} strokeWidth={2} />
            {!collapsed && (
              <>
                <span className="sidebar__label">{item.title}</span>
                <ChevronDown
                  className={`sidebar__chevron ${isOpen ? 'sidebar__chevron--open' : ''}`}
                  size={14}
                />
              </>
            )}
          </button>

          {isOpen && (
            <ul className={`sidebar__submenu ${collapsed ? 'sidebar__submenu--popup' : ''}`}>
              {item.subItems.map(sub => {
                const SubIcon = sub.icon;
                return (
                  <li key={sub.path}>
                    <NavLink
                      to={sub.path}
                      className={({ isActive }) =>
                        `sidebar__submenu-link ${isActive ? 'sidebar__submenu-link--active' : ''}`
                      }
                    >
                      <SubIcon size={16} strokeWidth={2} />
                      <span>{sub.title}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }

    // Item normal
    return (
      <li key={item.path} className="sidebar__menu-item">
        <NavLink
          to={item.path}
          className={({ isActive }) =>
            `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
          }
          data-tooltip={item.title}
        >
          <Icon className="sidebar__icon" size={20} strokeWidth={2} />
          {!collapsed && <span className="sidebar__label">{item.title}</span>}
          {item.badge && <span className="sidebar__badge">{item.badge}</span>}
        </NavLink>
      </li>
    );
  };

  return (
    <>
      {/* Overlay mobile */}
      {mobileOpen && (
        <div className="sidebar__overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Botón hamburguesa mobile */}
      <button
        className="sidebar__mobile-toggle"
        onClick={() => setMobileOpen(true)}
      >
        <PanelLeftOpen size={22} />
      </button>

      <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
        {/* Logo + Toggle */}
        <div className="sidebar__header">
          {!collapsed && (
            <div className="sidebar__logo-section" onClick={() => navigate('/dashboard')}>
              <img src={isDark ? LogoDark : LogoLight} alt="Posfac" className="sidebar__logo" />
            </div>
          )}
          <button
            className="sidebar__toggle"
            onClick={toggleCollapse}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>

        {/* Propiedad actual */}
        <div className="sidebar__property" title={propiedadNombre}>
          <button
            className="sidebar__property-button"
            onClick={handleCambiarPropiedad}
            data-tooltip={collapsed ? propiedadNombre : undefined}
          >
            <Building2 className="sidebar__property-icon" size={18} strokeWidth={2} />
            {!collapsed && (
              <>
                <span className="sidebar__property-name">{propiedadNombre}</span>
                <ArrowLeftRight size={14} className="sidebar__property-switch" />
              </>
            )}
          </button>
        </div>

        {/* Navegación */}
        <nav className="sidebar__nav">
          <ul className="sidebar__menu">
            {menuItems.map(renderNavItem)}
          </ul>
        </nav>

        {/* Sección inferior: tema + usuario */}
        <div className="sidebar__footer">
          {/* Toggle de tema */}
          <button
            className="sidebar__theme-toggle"
            onClick={toggleTheme}
            data-tooltip={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
            title={theme === 'light' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
          >
            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            {!collapsed && (
              <span>{theme === 'light' ? 'Modo oscuro' : 'Modo claro'}</span>
            )}
          </button>

          {/* Usuario */}
          {!userLoading && user && (
            <div className="sidebar__user" ref={userMenuRef}>
              <button
                className="sidebar__user-button"
                onClick={() => setShowUserMenu(prev => !prev)}
                data-tooltip={collapsed ? `${user.nombre} ${user.apellido || ''}` : undefined}
              >
                <div className="sidebar__user-avatar">
                  {user.foto_url ? (
                    <img src={user.foto_url} alt={user.nombre} />
                  ) : (
                    <span>{user.nombre.charAt(0)}{user.apellido?.charAt(0) || ''}</span>
                  )}
                </div>
                {!collapsed && (
                  <div className="sidebar__user-info">
                    <span className="sidebar__user-name">
                      {user.nombre} {user.apellido}
                    </span>
                    <span className="sidebar__user-role">{user.rol}</span>
                  </div>
                )}
              </button>

              {showUserMenu && (
                <div className={`sidebar__user-menu ${collapsed ? 'sidebar__user-menu--popup' : ''}`}>
                  <button
                    className="sidebar__user-menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      handleCambiarPropiedad();
                    }}
                  >
                    <Building2 size={16} />
                    <span>Cambiar Propiedad</span>
                  </button>
                  <button
                    className="sidebar__user-menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/mi-licencia');
                    }}
                  >
                    <KeyRound size={16} />
                    <span>Mi Licencia</span>
                  </button>
                  <div className="sidebar__user-menu-divider" />
                  <button
                    className="sidebar__user-menu-item sidebar__user-menu-item--logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
